/**
 * Pointing a check at something.
 *
 * FIVE OF THE TEN CHECKS WERE UNREACHABLE BEFORE THIS. They need a page or a list of
 * terms, they were created with neither, and there was no way to supply one — so they
 * refused on every run, once a day, forever, with nothing in the feed because nothing
 * had gone wrong. The pricing check in particular could never be exercised: nobody
 * could tell it which page the prices were on.
 *
 * SAYS WHEN RE-POINTING COSTS SOMETHING. Changing what a check watches starts its
 * history again — the server re-baselines, because diffing a new page against an old
 * page's state would report every tier withdrawn and every tier launched over an edit
 * the user made on purpose. Somebody correcting a typo should be told that before they
 * save rather than after, so the button says so.
 *
 * The form logic is in `probeFields.ts`, tested without React for the reason
 * `frontend-pure-logic-own-module` records.
 */
import { useEffect, useState } from 'react';
import api from '../../services/api';
import {
  engineOf,
  fieldsFor,
  geoOf,
  isRepointing,
  looksLikeUrl,
  parseTerms,
  sameStrings,
  termsOf,
  addTyped,
} from './probeFields';
import KeywordBadges from './KeywordBadges';
import type { WatchProbe } from '../../types';

/** As many keywords as a rank check reads (`MAX_KEYWORDS` in
 *  `jcseo/watch/detectors/rank.py`). The term box's limit of 10 cut a set of
 *  25 to 10 on save, and flagged every open form as starting again. */
const MAX_KEYWORDS = 25;

export default function ProbeEditor({
  probe,
  onSaved,
  onCancel,
}: {
  probe: WatchProbe;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const specs = fieldsFor(probe.kind);
  const [target, setTarget] = useState(probe.target);
  const [terms, setTerms] = useState(termsOf(probe).join(', '));
  const [geo, setGeo] = useState(geoOf(probe));
  // A rank check's searches, as words: loaded once, then edited as badges.
  const [loaded, setLoaded] = useState<string[] | null>(null);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [engine, setEngine] = useState(engineOf(probe));
  const [engines, setEngines] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const has = (field: string) => specs.some((s) => s.field === field);
  const wantsEngine = has('engine');
  const wantsKeywords = has('keywords');
  useEffect(() => {
    if (!wantsKeywords) return;
    let alive = true;
    api
      .getWatchProbeKeywords(probe.id)
      .then((rows) => {
        if (!alive) return;
        const words = rows.map((r) => r.value);
        setLoaded(words);
        setKeywords(words);
      })
      .catch(() => alive && setLoaded([]));
    return () => {
      alive = false;
    };
  }, [wantsKeywords, probe.id]);
  useEffect(() => {
    if (!wantsEngine) return;
    let alive = true;
    api
      .getWatchEngines()
      .then((d) => alive && setEngines(d.engines))
      .catch(() => alive && setEngines([]));
    return () => {
      alive = false;
    };
  }, [wantsEngine]);
  const next = {
    ...(has('url') ? { target } : {}),
    ...(has('terms') ? { terms: parseTerms(terms) } : {}),
    ...(has('geo') ? { geo } : {}),
    ...(has('engine') ? { engine } : {}),
  };
  // Text still in the box counts: Save takes it as a last badge.
  const finalKeywords = addTyped(keywords, draft, MAX_KEYWORDS, true).list;
  const repointing =
    isRepointing(probe, next) || (loaded !== null && !sameStrings(finalKeywords, loaded));

  const save = async () => {
    if (has('url') && target.trim() && !looksLikeUrl(target)) {
      setError('That does not look like a web address - it needs to start with https://');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const config: Record<string, unknown> = { ...probe.config };
      if (has('terms')) config.terms = parseTerms(terms);
      if (has('geo')) config.geo = geo.trim().toUpperCase();
      if (has('engine') && engine) config.engine = engine;
      await api.patchWatchProbe(probe.id, {
        ...(has('url') ? { target: target.trim() } : {}),
        ...(specs.some((s) => s.field !== 'url') ? { config } : {}),
        // As words; the server finds or makes each keyword. Not sent until
        // they have loaded, or an unloaded form would clear the set.
        ...(has('keywords') && loaded !== null ? { keywords: finalKeywords } : {}),
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not save');
    } finally {
      setSaving(false);
    }
  };

  if (!specs.length) return null;

  return (
    <div className="mt-2 space-y-2 rounded bg-gray-50 p-2 dark:bg-gray-800/50">
      {specs.map((spec) => {
        // Not a <label> round the badges: a click on its text would press the
        // first badge's × (a label clicks the first control inside it).
        const Wrap = spec.field === 'keywords' ? 'div' : 'label';
        return (
        <Wrap key={spec.field} className="block">
          <span className="block text-[11px] font-medium text-gray-700 dark:text-gray-200">
            {spec.label}
            {!spec.required && (
              <span className="ml-1 font-normal text-gray-400">optional</span>
            )}
          </span>
          {spec.field === 'engine' ? (
            <select
              value={engine}
              onChange={(e) => setEngine(e.target.value)}
              className="mt-0.5 w-full rounded border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-900"
            >
              {!engineOf(probe) && <option value="">{spec.placeholder}</option>}
              {/* The one it is set to, even if it has left the list. */}
              {Array.from(new Set([...(engineOf(probe) ? [engineOf(probe)] : []), ...engines])).map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          ) : spec.field === 'keywords' ? (
            <KeywordBadges
              list={keywords}
              draft={draft}
              limit={MAX_KEYWORDS}
              placeholder={spec.placeholder}
              loading={loaded === null}
              onChange={(list, d) => {
                setKeywords(list);
                setDraft(d);
              }}
            />
          ) : (
          <input
            type="text"
            value={
              spec.field === 'url'
                ? target
                : spec.field === 'terms'
                  ? terms
                  : geo
            }
            onChange={(e) => {
              const v = e.target.value;
              if (spec.field === 'url') setTarget(v);
              else if (spec.field === 'terms') setTerms(v);
              else setGeo(v);
            }}
            placeholder={spec.placeholder}
            className="mt-0.5 w-full rounded border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-900"
          />
          )}
          <span className="mt-0.5 block text-[11px] text-gray-500 dark:text-gray-400">
            {spec.hint}
          </span>
        </Wrap>
        );
      })}

      {error && (
        <p className="text-[11px] text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={saving}
          onClick={() => void save()}
          className="rounded bg-gray-900 px-2 py-1 text-[11px] text-white disabled:opacity-40 dark:bg-gray-100 dark:text-gray-900"
          /* The cost, said before the click rather than discovered after it. */
          title={
            repointing
              ? 'This check will start its history again, so the next run records rather than reports.'
              : undefined
          }
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-[11px] underline text-gray-500 dark:text-gray-400"
        >
          Cancel
        </button>
        {repointing && probe.is_baselined && (
          <span className="text-[11px] text-amber-700 dark:text-amber-300">
            Starts again from here - the next run records rather than reports.
          </span>
        )}
      </div>
    </div>
  );
}
